<template>
    <div class="prospective-patent-analysis display-flex flex-column w-100 h-100">
        <div class="output-header bg-moat-900 p-md">
            <h5 class="text-gold">{{ t('prospectivePatentAnalysis.playbookName') }}</h5>
            <p class="text-on-dark mt-sm">
                {{ t('prospectivePatentAnalysis.playbookDescription') }}
            </p>
        </div>
        <div class="workbench mt-sm flex-grow-1 overflow-auto">
            <AonRow class="workbench-card-holder flex-row h-100">
                <AonCol class="invention-context-col workbench-col grid-cols-4">
                    <AonCard class="invention-context-card workbench-card position-relative">
                        <AonCoverLoading :loading="processing" title="Running Analysis..." />
                        <h6>Invention Description</h6>
                        <p>
                            {{ t('prospectivePatentAnalysis.contextInstructionsPart1') }}
                        </p>
                        <p>
                            {{ t('prospectivePatentAnalysis.contextInstructionsPart2') }}
                        </p>
                        <FileUpload
                            :show-selected-file="false"
                            :allowed-types="allowedFileTypes"
                            @fileSelected="onFileUploaded"
                            @fileParsed="onFileParsed"
                            @fileParseError="onFileParseError"
                        />
                        <AonTextarea
                            v-model="patentContext"
                            :allow-resize="false"
                            :disabled="processing"
                            placeholder="Describe your invention or paste from a draft..."
                            name="txt-description"
                            id="txt-description"
                            class="display-flex flex-column h-100 flex-grow-1"
                            focus
                            :error="!!v$.patentContext.$errors.length"
                            :error-options="{
                                errorMsg: v$.patentContext.$errors?.[0]?.$message ?? null,
                            }"
                        />
                    </AonCard>
                </AonCol>
                <AonCol class="analysis-col workbench-col flex-grow-1">
                    <AonCard class="analysis-card workbench-card">
                        <h6>Results</h6>
                        <template v-if="!initialized">
                            <div class="initialize-cover flex-center flex-column h-100 w-100">
                                <h5 class="text-moat-900">
                                    Click Continue after entering your invention description.
                                </h5>
                                <AonButton
                                    label="Continue"
                                    :disabled="!patentContext || !!v$.patentContext.$errors.length"
                                    :tooltip-text="
                                        !patentContext
                                            ? 'Enter invention description before continuing'
                                            : null
                                    "
                                    @clicked="runAnalysis"
                                />
                            </div>
                        </template>
                        <template v-else>
                            <div
                                class="analysis-results-container display-flex flex-column h-100 w-100"
                            >
                                <AonTabs
                                    class="light mt-sm"
                                    :tabs="analysisResultsTabs"
                                    :current-tab="currentAnalysisResultsTab"
                                    bottom-border
                                    @tab-change="onResultsTabClicked"
                                />
                                <AonCard
                                    v-show="playbookStore.contextIsDirty"
                                    class="context-dirty-alert display-flex flex-row align-items-center"
                                >
                                    <font-awesome-icon
                                        icon="fas fa-triangle-exclamation"
                                        class=""
                                    />
                                    <span>Patent Details have changed since analysis was run.</span>
                                    <AonButton
                                        label="Update Analytics"
                                        type="subtle"
                                        @clicked="runAnalysis"
                                    />
                                </AonCard>

                                <ProspectivePatentAnalysisMicsTab
                                    v-if="currentAnalysisResultsTab === 1"
                                />
                                <ProspectivePatentAnalysisCompTab
                                    v-if="currentAnalysisResultsTab === 2"
                                />
                                <ProspectivePatentAnalysisPriorArtTab
                                    v-if="currentAnalysisResultsTab === 3"
                                />
                                <PatentLandscape
                                    v-if="currentAnalysisResultsTab === 4"
                                    :job-i-d="playbookStore.landscapeJobId"
                                    :loading="playbookStore.loadingStates.landscape.loading"
                                    @status="onPatentLandscapeStatus"
                                />
                            </div>
                        </template>
                    </AonCard>
                </AonCol>
            </AonRow>
        </div>
    </div>
</template>

<script setup>
import { computed, inject, onMounted, onUnmounted, reactive, ref } from 'vue'
import { useProspectivePatentPlaybookStore } from '@/stores/prospectivePatentPlaybook.js'
import { logger } from '@moatmetrics/vue-logger'
import { v4 as uuidv4 } from 'uuid'

import AonTextarea from '@moatmetrics/armory/src/components/base/forms/AonTextarea'
import ProspectivePatentAnalysisMicsTab from '@/components/playbooks/playbookTypes/prospectivePatentAnalysis/analysisTabs/ProspectivePatentAnalysisMicsTab.vue'
import ProspectivePatentAnalysisCompTab from '@/components/playbooks/playbookTypes/prospectivePatentAnalysis/analysisTabs/ProspectivePatentAnalysisCompTab.vue'
import ProspectivePatentAnalysisPriorArtTab from '@/components/playbooks/playbookTypes/prospectivePatentAnalysis/analysisTabs/ProspectivePatentAnalysisPriorArtTab.vue'
import PatentLandscape from '@/components/playbooks/playbookTypes/prospectivePatentAnalysis/charts/PatentLandscape.vue'
import FileUpload from '@/components/helpers/fileUpload.vue'

import { minLength, required } from '@vuelidate/validators'
import { useVuelidate } from '@vuelidate/core'
import { useI18n } from 'vue-i18n'
import { useFlag } from '@unleash/proxy-client-vue'

const landscapeFlag = useFlag('ipAlpha.landscapeBoard')

const { t } = useI18n()
const playbookStore = useProspectivePatentPlaybookStore()

const rumAgent = inject('rumAgent')
const eventBus = inject('eventBus')

const initialized = ref(false)
const currentAnalysisResultsTab = ref(1)
const previewRumView = ref(null)
const allowedFileTypes = ['.txt', '.docx', '.pdf']

const contextForm = reactive({
    patentContext: null,
})

const validationRules = {
    patentContext: {
        required,
        minLength: minLength(200),
    },
}

const v$ = useVuelidate(validationRules, contextForm)

const patentContext = computed({
    get: () => {
        contextForm.patentContext = playbookStore.patentContext
        return playbookStore.patentContext
    },
    set: (value) => {
        contextForm.patentContext = value
        playbookStore.patentContext = value
    },
})

const processing = computed(() => playbookStore.loading)

const analysisResultsTabs = computed(() => {
    return [
        {
            text: 'Moat MICS',
            value: 'mics',
            id: 1,
        },
        {
            text: 'Competition',
            value: 'competition',
            id: 2,
        },
        {
            text: 'Prior Art',
            value: 'priorArt',
            id: 3,
        },
        ...(landscapeFlag.value
            ? [
                  {
                      text: 'Landscape',
                      value: 'landscape',
                      id: 4,
                  },
              ]
            : []),
    ]
})

const runAnalysis = async () => {
    const isValid = await v$.value.$validate()
    if (!isValid) return

    initialized.value = true
    try {
        await playbookStore.runAllAnalytics(patentContext.value)
    } catch (error) {
        logger.error('Error running analysis:', error)
        eventBus.emit('snacktime', {
            type: 'error',
            message: t('prospectivePatentAnalysis.errors.overallError'),
            persistent: true,
        })
        initialized.value = false
    }
}

const onResultsTabClicked = (tab) => {
    currentAnalysisResultsTab.value = tab.id
    rumAgent.setView(tab.text)
}

const onFileUploaded = () => {}

const onFileParsed = (fileContent) => {
    patentContext.value = fileContent
    v$.value.patentContext.$touch()
}

const onFileParseError = (error) => {
    let persistent
    let message

    switch (error.id) {
        case 'invalid-file-type':
            message = t('prospectivePatentAnalysis.errors.invalidFileType', {
                fileType: error.fileType,
                allowedTypes: allowedFileTypes.join(', '),
            })
            persistent = false
            break

        default:
            message = t('prospectivePatentAnalysis.errors.generalFileParseError')
            persistent = false
    }

    eventBus.emit('snacktime', {
        type: 'error',
        message: message,
        persistent,
    })
}

const onPatentLandscapeStatus = (status) => {
    switch (status) {
        case 'FAILED':
            playbookStore.setLoadingState(
                'landscape',
                false,
                t('prospectivePatentAnalysis.errors.landscapeJobFailed')
            )
            break

        case 'JOB_COMPLETED':
            playbookStore.setLoadingState('landscape', false)
            break
    }
}

const setRumView = (tabName) => {
    let viewName = 'Prospective Patent Analysis Playbook'
    if (tabName) {
        viewName = `${viewName} - ${tabName}`
    }

    rumAgent.setView(viewName)
}

onMounted(async () => {
    previewRumView.value = rumAgent.getView()
    setRumView()
    playbookStore.setVirtualPatentId(uuidv4())
})

onUnmounted(() => {
    playbookStore.resetState()
})
</script>

<style scoped lang="scss">
.workbench {
    &-card-holder {
        gap: 20px;
    }

    &-col {
        padding: 0;
        min-width: 200px;

        &.analysis-col {
            width: 0;
        }
    }

    &-card {
        display: flex;
        height: 100%;
    }
}

.workbench-card {
    flex-direction: column;
    gap: 20px;

    h6 {
        border-bottom: solid var(--border-width-thin) var(--color-border-default);
    }
}

.invention-context-card {
    :deep(.input-wrapper) {
        flex-grow: 1;

        textarea {
            height: unset;
            max-height: unset;
        }
    }
}

.initialize-cover {
    gap: 20px;
}

.analysis-results-container {
    gap: 20px;
}

:deep(.a-cover-loading) {
    border-radius: var(--border-radius-lg);
    z-index: 10 !important;
}

.context-dirty-alert {
    padding: 0 20px !important;
    gap: 5px;
    border-color: var(--color-feedback-warning);
    background: var(--color-feedback-warning-bg);

    svg {
        color: var(--color-feedback-warning-dark);
    }

    :deep(button.ghost) {
        padding: 0;
        text-decoration: underline;
        font-weight: bold;
        color: var(--color-primary-dark);

        &:hover:not(:disabled) {
            background-color: transparent;
            text-decoration: none;
            color: var(--color-primary-darker);
        }
    }
}
</style>
